import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Plus, RefreshCw, CheckCircle2, AlertTriangle, Clock,
  Trash2, Info, ChevronDown, ChevronUp,
} from "lucide-react";

function CsdBadge({ status }) {
  const map = {
    active: "bg-green-50 text-green-700 border-green-200",
    expired: "bg-red-50 text-red-700 border-red-200",
    revoked: "bg-slate-100 text-slate-600 border-slate-200",
    superseded: "bg-slate-100 text-slate-500 border-slate-200",
    error: "bg-amber-50 text-amber-700 border-amber-200",
    pending: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const labels = {
    active: "Vigente",
    expired: "Vencido",
    revoked: "Revocado",
    superseded: "Reemplazado",
    error: "Error",
    pending: "Pendiente",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${map[status] || map.pending}`}>
      {labels[status] || status}
    </span>
  );
}

function RegisterForm({ entities, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    legal_entity_id: entities[0]?.id || "",
    cer_secret_name: "",
    key_secret_name: "",
    password_secret_name: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.legal_entity_id || !form.cer_secret_name || !form.key_secret_name || !form.password_secret_name) {
      toast.error("Todos los campos son obligatorios");
      return;
    }
    setLoading(true);
    try {
      await api.post("/ops/fiscal/csd/register", form);
      toast.success("CSD registrado y validado correctamente");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar CSD");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">Registrar nuevo CSD</h3>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex gap-2">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Los archivos .cer y .key los carga el equipo de Emergent como Custom Keys en el entorno.
          Ingresa los nombres exactos de esas variables (ej: <code>CSD_GALO_CER</code>).
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Entidad legal</label>
          <select
            data-testid="csd-entity-select"
            value={form.legal_entity_id}
            onChange={e => setForm(f => ({ ...f, legal_entity_id: e.target.value }))}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {entities.map(en => (
              <option key={en.id} value={en.id}>{en.legal_name} ({en.rfc})</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Nombre del secret .cer</label>
            <input
              data-testid="csd-cer-input"
              placeholder="CSD_GALO_CER_B64"
              value={form.cer_secret_name}
              onChange={e => setForm(f => ({ ...f, cer_secret_name: e.target.value.toUpperCase() }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Nombre del secret .key</label>
            <input
              data-testid="csd-key-input"
              placeholder="CSD_GALO_KEY_B64"
              value={form.key_secret_name}
              onChange={e => setForm(f => ({ ...f, key_secret_name: e.target.value.toUpperCase() }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Nombre del secret password</label>
            <input
              data-testid="csd-pwd-input"
              placeholder="CSD_GALO_PASSWORD"
              value={form.password_secret_name}
              onChange={e => setForm(f => ({ ...f, password_secret_name: e.target.value.toUpperCase() }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            data-testid="btn-registrar-csd"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <RefreshCw className="w-3 h-3 animate-spin" />}
            Registrar y validar CSD
          </button>
          <button type="button" onClick={onCancel} className="border border-slate-300 rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

export default function OpsFiscalCsd() {
  const [csds, setCsds] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [validating, setValidating] = useState(null);

  const load = useCallback(async () => {
    try {
      const [csdRes, entRes] = await Promise.all([
        api.get("/ops/fiscal/csd"),
        api.get("/ops/legal-entities"),
      ]);
      setCsds(csdRes.data.csds || []);
      const ents = entRes.data;
      setEntities(Array.isArray(ents) ? ents : ents.entities || ents.legal_entities || []);
    } catch (e) {
      toast.error("Error al cargar certificados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleValidate = async (csdId) => {
    setValidating(csdId);
    try {
      const res = await api.post(`/ops/fiscal/csd/${csdId}/validate`);
      toast.success(`Validado: ${res.data.status} — ${res.data.days_until_expiry ?? "?"} días restantes`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al validar");
    } finally {
      setValidating(null);
    }
  };

  const handleRevoke = async (csdId) => {
    if (!window.confirm("¿Seguro que quieres revocar este CSD? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/ops/fiscal/csd/${csdId}`);
      toast.success("CSD marcado como revocado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al revocar");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Certificados de Sello Digital
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">CSD por entidad legal — necesarios para emitir CFDI 4.0</p>
        </div>
        <button
          data-testid="btn-nuevo-csd"
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Registrar CSD
        </button>
      </div>

      {showForm && (
        <RegisterForm
          entities={entities}
          onSuccess={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-12 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Cargando...
        </div>
      ) : csds.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No hay CSDs registrados</p>
          <p className="text-sm text-slate-400 mt-1">
            Carga los archivos .cer y .key de cada razón social como Custom Keys en Emergent,
            luego registralos aqui.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {csds.map(csd => (
            <div key={csd.id} data-testid={`csd-row-${csd.id}`} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-800 text-sm">{csd.legal_name || csd.rfc}</p>
                    <CsdBadge status={csd.status} />
                    {csd.days_until_expiry <= 30 && csd.status === "active" && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                        Vence en {csd.days_until_expiry}d
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 font-mono">RFC: {csd.rfc}</p>
                  <p className="text-xs text-slate-400">
                    Serial: {csd.serial_number} — Vence: {csd.expiration_date}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Secrets: {csd.cer_secret_name} / {csd.key_secret_name}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`btn-validate-csd-${csd.id}`}
                    onClick={() => handleValidate(csd.id)}
                    disabled={validating === csd.id}
                    className="text-xs border border-slate-300 rounded-lg px-3 py-1.5 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                  >
                    {validating === csd.id
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <CheckCircle2 className="w-3 h-3" />
                    }
                    Validar
                  </button>
                  {csd.status !== "revoked" && (
                    <button
                      data-testid={`btn-revoke-csd-${csd.id}`}
                      onClick={() => handleRevoke(csd.id)}
                      className="text-xs border border-red-200 text-red-600 rounded-lg px-3 py-1.5 hover:bg-red-50 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Revocar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
